import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Upload, X, Image as ImageIcon } from "lucide-react";

interface MCQImageUploadProps {
  label: string;
  value: string | null;
  onChange: (url: string | null) => void;
  pathPrefix?: string;
}

export const MCQImageUpload = ({ label, value, onChange, pathPrefix = "mcq" }: MCQImageUploadProps) => {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid file", description: "Please select an image", variant: "destructive" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Image too large", description: "Max 5MB", variant: "destructive" });
      return;
    }
    setUploading(true);
    const ext = file.name.split(".").pop() ?? "png";
    const path = `${pathPrefix}/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("mcq-images").upload(path, file, { upsert: false });
    if (error) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
      setUploading(false);
      return;
    }
    const { data } = supabase.storage.from("mcq-images").getPublicUrl(path);
    onChange(data.publicUrl);
    setUploading(false);
    toast({ title: "Image uploaded" });
  };

  return (
    <div className="grid gap-2">
      <label className="text-sm font-medium">{label}</label>
      {value ? (
        <div className="relative inline-block">
          <img src={value} alt={label} className="max-h-40 rounded-md border" />
          <Button
            type="button"
            size="icon"
            variant="destructive"
            className="absolute -top-2 -right-2 h-6 w-6"
            onClick={() => onChange(null)}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      ) : (
        <label className="flex items-center gap-2 cursor-pointer rounded-md border border-dashed p-4 hover:bg-muted/50 text-sm text-muted-foreground">
          {uploading ? <Upload className="h-4 w-4 animate-pulse" /> : <ImageIcon className="h-4 w-4" />}
          <span>{uploading ? "Uploading..." : "Click to upload image (max 5MB)"}</span>
          <Input
            type="file"
            accept="image/*"
            className="hidden"
            disabled={uploading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
          />
        </label>
      )}
    </div>
  );
};
